import React,{useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {Helmet} from 'react-helmet-async';
import { FiArrowLeft, FiUsers, FiCheck, FiZap, FiInfo, FiUser, FiShield } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useAuth} from '../contexts/AuthContext';
import AvatarBadge from '../components/AvatarBadge';

function Members({groups}) {
  const navigate=useNavigate();
  const {user}=useAuth();
  const [selectedMembers,setSelectedMembers]=useState([]);
  const [selectedGroupId,setSelectedGroupId]=useState(null);
  const [autoSelectedUser,setAutoSelectedUser]=useState(false); // Track if user was auto-selected

  // Reset selections when groups change
  useEffect(()=> {
    setSelectedMembers([]);
    setSelectedGroupId(null);
    setAutoSelectedUser(false);
  },[groups]);

  const handleMemberSelect=(member,groupId)=> {
    // If selecting from a different group, clear previous selections
    if (selectedGroupId && selectedGroupId !== groupId) {
      // Find current user in the new group
      const newGroup=groups.find(g=> g.id===groupId);
      const newGroupUserMember=newGroup?.members.find(m=> m.userId===user?.id);
      
      if (member.userId===user?.id) {
        // User is selecting themselves in new group
        setSelectedMembers([member]);
        setAutoSelectedUser(false);
      } else {
        // User is selecting someone else in new group - auto-include themselves
        const newSelections=[member];
        if (newGroupUserMember) {
          newSelections.push(newGroupUserMember);
          setAutoSelectedUser(true);
        } else {
          setAutoSelectedUser(false);
        }
        setSelectedMembers(newSelections);
      }
      
      setSelectedGroupId(groupId);
      return;
    }

    // If first selection, set the group
    if (!selectedGroupId) {
      setSelectedGroupId(groupId);
    }

    // Handle selection within the same group
    const isSelected=selectedMembers.some(m=> m.userId===member.userId);
    const isCurrentUser=member.userId===user?.id;
    
    if (isSelected) {
      // Deselecting a member
      const newSelection=selectedMembers.filter(m=> m.userId !== member.userId);
      
      if (isCurrentUser) {
        // User is deselecting themselves
        setAutoSelectedUser(false);
      } else {
        // User is deselecting someone else
        const otherMembersSelected=newSelection.filter(m=> m.userId !== user?.id);
        
        // FIXED: Auto-deselect current user if NO other members are selected (not 2+)
        if (otherMembersSelected.length===0 && autoSelectedUser) {
          // No other members left and user was auto-selected - remove user too
          const finalSelection=newSelection.filter(m=> m.userId !== user?.id);
          setSelectedMembers(finalSelection);
          setAutoSelectedUser(false);
          
          // If no members left, clear group selection
          if (finalSelection.length===0) {
            setSelectedGroupId(null);
          }
          return;
        }
      }
      
      setSelectedMembers(newSelection);
      
      // If no members left, clear group selection
      if (newSelection.length===0) {
        setSelectedGroupId(null);
        setAutoSelectedUser(false);
      }
    } else {
      // Selecting a member
      const newSelection=[...selectedMembers,member];
      
      if (!isCurrentUser) {
        // FIXED: Auto-select current user when ANY other member is selected (not just 2+)
        const userAlreadySelected=selectedMembers.some(m=> m.userId===user?.id);
        
        if (!userAlreadySelected) {
          // Find current user in the current group
          const currentGroup=groups.find(g=> g.id===groupId);
          const currentUserMember=currentGroup?.members.find(m=> m.userId===user?.id);
          
          if (currentUserMember) {
            newSelection.push(currentUserMember);
            setAutoSelectedUser(true);
            
            // Trigger visual feedback animation
            setTimeout(()=> {
              const userCard=document.querySelector(`[data-member-id="${user.id}"][data-group-id="${groupId}"]`);
              if (userCard) {
                userCard.classList.add('auto-selected-flash');
                setTimeout(()=> {
                  userCard.classList.remove('auto-selected-flash');
                },600);
              }
            },50);
          }
        }
      } else {
        // User is manually selecting themselves
        setAutoSelectedUser(false);
      }
      
      setSelectedMembers(newSelection);
    }
  };

  const handleScheduleRound=()=> {
    if (selectedGroupId && selectedMembers.length > 0) {
      // Navigate to create round with selected group and members
      navigate(`/create-round/${selectedGroupId}?members=${selectedMembers.map(m=> m.userId).join(',')}`);
    }
  };

  const getRoleBadge=(role)=> {
    switch (role) {
      case 'owner':
        return {text: 'Owner',className: 'bg-accent/10 text-accent border-accent',icon: FiShield};
      case 'admin':
        return {text: 'Admin',className: 'bg-blue-100 text-blue-800 border-blue-200',icon: FiShield};
      default:
        return {text: 'Member',className: 'bg-surface text-text-primary border-border-subtle',icon: FiUser};
    }
  };

  const containerVariants={
    hidden: {opacity: 0},
    visible: {opacity: 1,transition: {staggerChildren: 0.1}}
  };

  const itemVariants={
    hidden: {y: 30,opacity: 0},
    visible: {y: 0,opacity: 1,transition: {type: "spring",stiffness: 100,damping: 15}}
  };

  // Filter out current user's own membership from each group for display purposes
  // but keep track of all members including current user for selection logic
  const groupsWithFilteredMembers=groups.map(group=> ({
    ...group,
    filteredMembers: group.members.filter(member=> member.role !== 'self'), // Remove any 'self' indicators
    allMembers: group.members // Keep all members for selection logic
  }));

  const totalMembers=groupsWithFilteredMembers.reduce((sum,group)=> sum + group.filteredMembers.length,0);

  if (!groups || groups.length===0) {
    return (
      <div className="min-h-screen bg-app">
        <Helmet>
          <title>Members - TeeSync</title>
        </Helmet>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
          <motion.div
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.5}}
          >
            <div className="mb-8">
              <button
                onClick={()=> navigate('/')}
                className="flex items-center text-accent hover:text-accent mb-6 group transition-all duration-200"
              >
                <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="font-semibold">Back to Dashboard</span>
              </button>
              
              <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
                Group Members
              </h1>
            </div>

            <div className="text-center py-16 bg-card rounded-lg border border-border-subtle">
              <div className="w-24 h-24 bg-surface rounded-lg flex items-center justify-center text-3xl mx-auto mb-6">
                <SafeIcon icon={FiUsers} className="w-12 h-12 text-text-muted" />
              </div>
              <h3 className="text-xl font-bold text-text-primary mb-3">
                No Groups Found
              </h3>
              <p className="text-text-secondary mb-8 font-medium max-w-md mx-auto">
                Join or create golf groups to see and schedule rounds with members
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Members - TeeSync</title>
      </Helmet>
      {/* Add CSS for auto-selection animation */}
      <style jsx>{`
        .auto-selected-flash {
          animation: autoSelectFlash 0.6s ease-out;
        }
        
        @keyframes autoSelectFlash {
          0% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 0 0 10px rgba(34, 197, 94, 0.1);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
          }
        }
      `}</style>

      <div className="min-h-screen bg-app">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            {/* Header */}
            <motion.div variants={itemVariants}>
              <div className="mb-8">
                <button
                  onClick={()=> navigate('/')}
                  className="flex items-center text-accent hover:text-accent mb-6 group transition-all duration-200"
                >
                  <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                  <span className="font-semibold">Back to Dashboard</span>
                </button>
                
                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
                  Group Members
                </h1>
                <p className="text-base lg:text-lg text-text-secondary font-medium">
                  Select members to schedule a round ({totalMembers} members across {groups.length} groups)
                </p>
              </div>
            </motion.div>

            {/* Selection Info */}
            {selectedMembers.length > 0 && (
              <motion.div
                variants={itemVariants}
                initial={{opacity: 0,y: -20}}
                animate={{opacity: 1,y: 0}}
                className="bg-accent/10 border border-accent rounded-lg p-6"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-accent mb-2">
                      {selectedMembers.length} member{selectedMembers.length !== 1 ? 's' : ''} selected
                    </h3>
                    <p className="text-accent">
                      From: {groups.find(g=> g.id===selectedGroupId)?.name || 'Unknown Group'}
                      {autoSelectedUser && (
                        <span className="ml-2 text-accent font-medium">
                          • You were automatically included
                        </span>
                      )}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                    onClick={handleScheduleRound}
                    className="flex items-center px-6 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover transition-all duration-200 font-semibold "
                  >
                    <SafeIcon icon={FiZap} className="w-5 h-5 mr-2" />
                    New Round
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Enhanced Constraint Info */}
            <motion.div variants={itemVariants}>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-start">
                  <SafeIcon icon={FiInfo} className="w-6 h-6 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-2">Selection Guidelines</h4>
                    <ul className="text-blue-800 space-y-1 text-sm">
                      <li>• You can only select members from one group at a time</li>
                      <li>• <strong>You'll be automatically included</strong> when you select other members</li>
                      <li>• You can uncheck yourself if scheduling for others only</li>
                      <li>• Switching groups will clear previous selections</li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Groups and Members */}
            <div className="space-y-8">
              {groupsWithFilteredMembers.map((group,groupIndex)=> (
                <motion.div
                  key={group.id}
                  variants={itemVariants}
                  transition={{delay: groupIndex * 0.1}}
                  className="bg-card rounded-lg border border-border-subtle p-8"
                >
                  {/* Group Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-text-primary mb-2">{group.name}</h2>
                      <p className="text-text-secondary font-medium">
                        {group.allMembers.length} member{group.allMembers.length !== 1 ? 's' : ''} total
                        {group.userRole && (
                          <span className="ml-2 text-accent">
                            • You are {group.userRole}
                          </span>
                        )}
                      </p>
                    </div>
                    
                    {selectedGroupId===group.id && (
                      <div className="flex items-center text-accent bg-accent/10 px-4 py-2 rounded-xl border border-accent">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                        <span className="font-semibold text-sm">Active Selection</span>
                      </div>
                    )}
                  </div>

                  {/* Members Grid - Show ALL members including current user */}
                  {group.allMembers.length > 0 ? (
                    <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {group.allMembers.map((member,memberIndex)=> {
                        // FIXED: Check selection based on BOTH userId AND groupId to prevent cross-group selection
                        const isSelected=selectedMembers.some(m=> m.userId===member.userId) && selectedGroupId===group.id;
                        const isDisabled=selectedGroupId && selectedGroupId !== group.id;
                        const isCurrentUser=member.userId===user?.id;
                        const roleBadge=getRoleBadge(member.role);
                        // REMOVED: wasAutoSelected variable and associated logic

                        return (
                          <motion.div
                            key={member.userId || memberIndex}
                            initial={{opacity: 0,scale: 0.9}}
                            animate={{opacity: 1,scale: 1}}
                            transition={{delay: memberIndex * 0.05}}
                            data-member-id={member.userId}
                            data-group-id={group.id} // ADDED: Group ID for scoped selection
                            className={`relative border rounded-lg p-4 transition-all duration-200 cursor-pointer ${
                              isSelected
                                ? 'border-accent bg-accent/10 '
                                : isDisabled
                                ? 'border-border-subtle bg-surface opacity-50 cursor-not-allowed'
                                : 'border-border-subtle bg-card hover:border-border-subtle hover:'
                            }`}
                            onClick={()=> !isDisabled && handleMemberSelect(member,group.id)}
                          >
                            {/* Selection Checkbox */}
                            <div className="absolute top-3 right-3">
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                                isSelected
                                  ? 'border-accent bg-accent/100'
                                  : isDisabled
                                  ? 'border-border-subtle bg-surface'
                                  : 'border-border-subtle bg-card'
                              }`}>
                                {isSelected && (
                                  <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                                )}
                              </div>
                            </div>

                            {/* REMOVED: Auto-selected indicator */}

                            {/* Member Info */}
                            <div className="pr-8">
                              <div className="flex items-center mb-3">
                                <AvatarBadge
                                  name={typeof member==='string' ? member : member.name}
                                  size="lg"
                                  selected={isCurrentUser}
                                  className="mr-3"
                                />
                                <div>
                                  <h3 className="font-semibold text-text-primary">
                                    {typeof member==='string' ? member : member.name}
                                    {isCurrentUser && (
                                      <span className="ml-2 text-green-600 text-sm font-medium">
                                        (You)
                                      </span>
                                    )}
                                  </h3>
                                  <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${roleBadge.className}`}>
                                    <SafeIcon icon={roleBadge.icon} className="w-3 h-3 mr-1" />
                                    {roleBadge.text}
                                  </div>
                                </div>
                              </div>
                              
                              {/* REMOVED: Auto-selection hint */}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-text-muted">
                      <SafeIcon icon={FiUsers} className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No members in this group</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Floating Schedule Button (Mobile) */}
            {selectedMembers.length > 0 && (
              <motion.div
                initial={{opacity: 0,y: 100}}
                animate={{opacity: 1,y: 0}}
                className="fixed bottom-6 right-6 z-50 md:hidden"
              >
                <motion.button
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                  onClick={handleScheduleRound}
                  className="w-14 h-14 bg-accent text-white rounded-full  flex items-center justify-center hover:bg-interactive-hover transition-all duration-200"
                >
                  <SafeIcon icon={FiZap} className="w-6 h-6" />
                </motion.button>
                
                {/* Selection Count Badge */}
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {selectedMembers.length}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Members;